import React, { useState, useEffect } from 'react';
import { Copy, Check, Clock, UserPlus, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ProviderManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/roadside/admin/signup-tokens`);
      setTokens(response.data.tokens);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      toast.error('Failed to load signup tokens');
    }
  };

  const generateLink = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/roadside/admin/generate-provider-link`,
        null,
        { params: { admin_username: 'admin' } }
      );
      
      setGeneratedLink(response.data);
      toast.success('Signup link generated successfully!');
      
      // Refresh tokens list
      await fetchTokens();
    } catch (error) {
      console.error('Error generating link:', error);
      toast.error('Failed to generate signup link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, tokenId) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(tokenId);
    toast.success('Link copied to clipboard!');
    
    setTimeout(() => {
      setCopiedToken(null);
    }, 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Used':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Expired':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Provider Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generate secure signup links for new service providers
          </p>
        </div>
        <Button
          onClick={generateLink}
          disabled={isLoading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {isLoading ? 'Generating...' : 'Generate Signup Link'}
        </Button>
      </div>

      {/* Recently Generated Link */}
      {generatedLink && (
        <Card className="p-6 border-2 border-blue-200 bg-blue-50">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <ExternalLink className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2">✅ New Signup Link Generated</h3>
              
              <div className="bg-white rounded-lg p-4 mb-3 border border-blue-200">
                <p className="text-xs text-gray-600 mb-1">Signup URL:</p>
                <p className="text-sm font-mono text-gray-900 break-all">
                  {generatedLink.signup_url}
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() => copyToClipboard(generatedLink.signup_url, generatedLink.token)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {copiedToken === generatedLink.token ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </>
                  )}
                </Button>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Expires in 24 hours</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-xs text-yellow-800">
                    <p className="font-semibold mb-1">📧 Email Sending (Mocked for MVP)</p>
                    <p>In production, this link would be automatically emailed to the provider. For now, please copy and share the link manually.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tokens List */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Signup Link History
          {tokens.length > 0 && (
            <span className="text-sm font-normal text-gray-600 ml-2">
              ({tokens.length} total)
            </span>
          )}
        </h3>

        {tokens.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No signup links generated yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Click "Generate Signup Link" to create your first provider invitation
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tokens.map((token, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={`${getStatusColor(token.status)} border`}>
                      {token.status}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      Created {formatDate(token.created_at)}
                    </span>
                  </div>

                  <div className="text-sm space-y-1">
                    <p className="text-gray-600">
                      <span className="font-semibold">Token:</span>{' '}
                      <code className="bg-white px-2 py-0.5 rounded text-xs">
                        {token.token.substring(0, 20)}...
                      </code>
                    </p>

                    {token.is_used && (
                      <p className="text-gray-600">
                        <span className="font-semibold">Used by:</span> {token.used_by_email}
                        {' '}({formatDate(token.used_at)})
                      </p>
                    )}

                    {!token.is_used && !token.is_expired && (
                      <p className="text-gray-600">
                        <span className="font-semibold">Expires:</span> {formatDate(token.expires_at)}
                      </p>
                    )}
                  </div>
                </div>

                {token.status === 'Active' && (
                  <Button
                    onClick={() => {
                      const url = `${window.location.origin}/provider/signup?token=${token.token}`;
                      copyToClipboard(url, token.token);
                    }}
                    size="sm"
                    variant="outline"
                  >
                    {copiedToken === token.token ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
